// src/features/auth/LineAuthCallback.jsx
// LIFF のエンドポイント。LINEログインの着地点で、URL は 2通りの用途を持つ。
//   /auth/line?r=/            … LINEでログイン（未ログインの人）
//   /auth/line?link=<ticket>  … ログイン済みアカウントに LINE を連携（チケット経由）
//
// ⚠このページは**必ず** liff.line.me/{LIFF_ID} 経由で開かれる（LIFF の endpoint URL に
//   登録するのはこのパス）。直叩きでは LIFF ID が無いので「開き直してください」になる。
// ⚠LINEアプリ内ブラウザは外部ブラウザとストレージが別。復帰先(r)や連携チケットは
//   sessionStorage では渡らないので URL で運ぶ。
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithCustomToken,
  signInWithPhoneNumber,
  RecaptchaVerifier,
} from "firebase/auth";
import { AlertTriangle, RefreshCw, Check, Phone, MessageCircle } from "lucide-react";

import Loader from "../../components/Loader";
import { auth } from "../../firebase";
import { TENANT_ID } from "../../tenant";
import { ensureCustomer } from "../../lib/ensureCustomer";
import { acquireLineAccessToken, liffAwareQuery, stripAuthParams } from "../../lib/liff";
import { savePendingIntent } from "../../lib/pendingIntent";
import { loginWithLine, linkLineToCurrentUser } from "../../lib/lineAuthApi";

const RECAPTCHA_ID = "recaptcha-line-register";

/** 日本の携帯番号を E.164 へ。 */
function toE164JP(raw) {
  const d = String(raw || "").replace(/\D/g, "");
  return d.startsWith("0") ? `+81${d.slice(1)}` : `+81${d}`;
}

/**
 * 無言で固まらせないための時間切れ。
 * ⚠LINE の webview では Firebase の Auth 初期化や端末内保存領域の読み書きが
 *   「成功も失敗もしない」状態になることがある。待ち続けると手がかりが残らない。
 */
function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_r, rej) => setTimeout(() => rej(new Error(`TIMEOUT:${label}`)), ms)),
  ]);
}

/**
 * 端末内の保存領域が使えるかを短時間で測る。LINE アプリ内ブラウザでは
 * IndexedDB が「開きも失敗もしない」ことがあり、それが Auth 初期化の停止に直結する。
 */
async function probeStorage() {
  const out = [];
  try {
    localStorage.setItem("__probe", "1");
    localStorage.removeItem("__probe");
    out.push("ls=ok");
  } catch (_e) {
    out.push("ls=ng");
  }
  try {
    await Promise.race([
      new Promise((res, rej) => {
        const req = window.indexedDB.open("__probe", 1);
        req.onsuccess = () => {
          try { req.result.close(); } catch (_e2) { /* noop */ }
          res();
        };
        req.onerror = () => rej(new Error("err"));
        req.onblocked = () => rej(new Error("blocked"));
      }),
      new Promise((_r, rej) => setTimeout(() => rej(new Error("timeout")), 3000)),
    ]);
    out.push("idb=ok");
  } catch (e) {
    out.push(`idb=${e?.message || "ng"}`);
  }
  return out.join(" ");
}

/** 復帰先はオープンリダイレクトにならないよう、自サイト内のパスだけ許す。 */
function safeReturnPath(raw) {
  const v = String(raw || "").trim();
  if (!v.startsWith("/")) return "/";
  if (v.startsWith("//")) return "/";
  return v;
}

export default function LineAuthCallback() {
  const navigate = useNavigate();
  const startedRef = useRef(false);
  // 未登録だったときの登録ステップで使い回す（LIFFトークンは同一セッション内なら有効）
  const accessTokenRef = useRef("");
  const returnToRef = useRef("/");
  const tenantIdRef = useRef(TENANT_ID);

  const [error, setError] = useState("");
  const [mode, setMode] = useState("login");
  const [linked, setLinked] = useState(false);
  // 止まったときに「どこで止まったか」を画面に出す。LINEアプリ内ブラウザは
  // 開発者ツールが使えないので、これが唯一の手がかりになる。
  const [stage, setStage] = useState("start");
  const [lineName, setLineName] = useState("");

  // 未登録 LINE の登録ステップ（電話番号 → SMS → LINE連携）
  const [regStep, setRegStep] = useState("PHONE"); // PHONE | OTP
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [regError, setRegError] = useState("");
  const verifierRef = useRef(null);
  const confirmationRef = useRef(null);

  useEffect(() => {
    // StrictMode の二重実行で LINE の認可コードを二度使わないよう1回に固定する
    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;

    (async () => {
      const q = liffAwareQuery();
      const returnTo = safeReturnPath(q.get("r"));
      const linkTicket = (q.get("link") || "").trim();
      const spaceId = (q.get("spaceId") || "").trim();
      // ⚠テナントはURLで運ばれてくる。LIFF のエンドポイントは全テナント共通で1本
      //   (`https://go.akuto.app/auth/line`)なので、パスからは拠点が判らない。
      //   付いていなければホスト名から決まる既定値(=suomi)にフォールバックする。
      const tenantId = (q.get("t") || "").trim() || TENANT_ID;
      tenantIdRef.current = tenantId;
      const devUserId = (q.get("dev") || "").trim();

      if (linkTicket) setMode("link");

      try {
        const acquired = await acquireLineAccessToken({
          devUserId,
          key: "spaceos-login",
          onStage: (s) => {
            if (!cancelled) setStage(s);
          },
        });
        if (!acquired || cancelled) return; // ログイン/リロードへ遷移した

        const { accessToken } = acquired;

        if (linkTicket) {
          setStage("link");
          await withTimeout(
            linkLineToCurrentUser({
              tenantId,
              accessToken,
              spaceId,
              linkTicket,
            }),
            25000,
            "link"
          );

          stripAuthParams();
          // 連携はここで完了。この画面は LINE アプリ内ブラウザなので、黙って
          // ダッシュボードへ飛ばすより「終わった」とはっきり見せる方が迷わない。
          if (!cancelled) setLinked(true);
          return;
        }

        setStage("signin");
        accessTokenRef.current = accessToken;
        returnToRef.current = returnTo;

        const result = await withTimeout(
          loginWithLine({ tenantId, accessToken }),
          25000,
          "signin"
        );

        // 未登録の LINE。アカウントは作られていない（鍵は電話番号ただ1つ）。
        // このまま電話番号+SMS認証へ進み、作成後に LINE を結びつける。
        if (result?.registered === false) {
          stripAuthParams();
          if (!cancelled) {
            setLineName(result?.displayName || "");
            setMode("register");
          }
          return;
        }

        const customToken = result?.customToken;
        if (!customToken) throw new Error("ログインできませんでした。");

        // ここから先は Auth を使う。LINE の webview で Auth の初期化が詰まると
        // ここで止まるので、段階を分けて時間切れも入れる。
        setStage("auth");
        await withTimeout(signInWithCustomToken(auth, customToken), 25000, "auth");

        // ログイン前の意図（購入・予約・ギフト受取）を URL から復元する。
        // sessionStorage の pendingIntent は外部ブラウザ側に残っていてここには届かない。
        const intentType = (q.get("i") || "").trim();
        if (intentType) {
          savePendingIntent({
            type: intentType,
            ...(q.get("tk") ? { token: q.get("tk") } : {}),
            returnTo: returnTo,
          });
        }

        setStage("customer");
        try {
          await withTimeout(ensureCustomer(tenantId), 20000, "customer");
        } catch (e) {
          // Stripe 顧客の用意に失敗しても、ログイン自体は成立させる（購入時に再試行される）
          console.error("[LineAuthCallback] ensureCustomer error:", e);
        }

        stripAuthParams();
        if (!cancelled) navigate(returnTo, { replace: true });
      } catch (e) {
        console.error("[LineAuthCallback] failed:", e);
        if (cancelled) return;

        const msg = String(e?.message || "");

        if (msg === "TIMEOUT:auth") {
          // Auth の初期化が終わらないケース。原因の切り分けに、端末内保存領域の
          // 可否をその場で測って添える（LINEアプリ内ブラウザは開発者ツールが無いため）。
          const probe = await probeStorage();
          setError(
            "ログインの仕上げが時間内に終わりませんでした。" +
              "この画面を閉じて、もう一度開いてみてください。" +
              `（診断: ${probe}）`
          );
          return;
        }

        if (msg.startsWith("TIMEOUT:")) {
          // ⚠LINE webview は init が解決も失敗もしないことがある。固まらせずここへ落とす。
          setError(
            `LINEとの通信が時間内に終わりませんでした（${msg.slice(8)}）。` +
              "この画面を一度閉じて、もう一度開いてみてください。"
          );
          return;
        }

        if (e?.message === "LINK_TICKET_INVALID") {
          setError("連携の有効期限が切れました。アプリに戻って、もう一度お試しください。");
        } else if (e?.message === "LINE_LINKED_TO_OTHER_ACCOUNT") {
          setError(
            "このLINEアカウントは、別のご利用アカウントに連携済みです。" +
              "そちらのアカウントでログインしてご利用ください。"
          );
        } else {
          setError(e?.message || "LINEログインに失敗しました。");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (busy) return;

    setRegError("");
    setBusy(true);

    try {
      if (!verifierRef.current) {
        verifierRef.current = new RecaptchaVerifier(auth, RECAPTCHA_ID, {
          size: "invisible",
        });
        await verifierRef.current.render();
      }

      confirmationRef.current = await signInWithPhoneNumber(
        auth,
        toE164JP(phone),
        verifierRef.current
      );
      setRegStep("OTP");
    } catch (err) {
      console.error("[LineAuthCallback] signInWithPhoneNumber failed:", err);
      setRegError(
        err?.code === "auth/too-many-requests"
          ? "認証リクエストが集中しています。少し時間をおいてお試しください。"
          : `SMSを送れませんでした（${err?.code || "unknown"}）`
      );
      try {
        verifierRef.current?.clear();
      } catch (_e) {
        /* noop */
      }
      verifierRef.current = null;
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmCode = async (e) => {
    e.preventDefault();
    if (busy) return;

    setRegError("");
    setBusy(true);

    try {
      // ① 電話番号で本人確認 → ここで初めてアカウントが出来る（既存の番号なら既存アカウントに入る）
      await confirmationRef.current.confirm(otp);

      // ② そのアカウントに LINE を結びつける。以後その LINE でログインすると同じアカウントに入る。
      //    ⚠ここはログイン済みなので Auth 付きの app で呼ぶ（request.auth.uid を使わせる）
      await withTimeout(
        linkLineToCurrentUser({
          tenantId: tenantIdRef.current,
          accessToken: accessTokenRef.current,
          authed: true,
        }),
        25000,
        "link"
      );

      // ③ Stripe 顧客など。失敗してもログインは成立させる（購入時に再試行される）
      try {
        await withTimeout(ensureCustomer(tenantIdRef.current), 20000, "customer");
      } catch (e2) {
        console.error("[LineAuthCallback] ensureCustomer error:", e2);
      }

      navigate(returnToRef.current || "/", { replace: true });
    } catch (err) {
      console.error("[LineAuthCallback] register failed:", err);

      if (String(err?.message) === "LINE_LINKED_TO_OTHER_ACCOUNT") {
        setRegError(
          "このLINEアカウントは、別のご利用アカウントに連携済みです。" +
            "そちらのアカウントでログインしてご利用ください。"
        );
      } else {
        setRegError("認証コードが正しくありません。もう一度お試しください。");
      }
    } finally {
      setBusy(false);
    }
  };

  if (mode === "register") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5 py-8">
        <div className="w-full max-w-md bg-white rounded-3xl border border-gray-100 p-6">
          <div id={RECAPTCHA_ID} />

          <div className="w-14 h-14 rounded-3xl bg-[#06C755]/10 flex items-center justify-center mb-5">
            <MessageCircle className="text-[#06C755]" size={28} />
          </div>

          <h1 className="text-xl font-black text-gray-900">
            {lineName ? `${lineName} さん、はじめまして` : "はじめてのご利用ですね"}
          </h1>

          <p className="mt-3 text-sm leading-7 text-gray-600 font-medium">
            ご利用開始には電話番号の確認が必要です。
            一度だけSMSで認証すると、次回からはLINEでそのまま入れます。
          </p>

          {regError && (
            <div className="mt-4 bg-red-50 text-red-700 rounded-3xl px-4 py-3 text-sm font-bold">
              {regError}
            </div>
          )}

          {regStep === "PHONE" ? (
            <form onSubmit={handleSendCode} className="mt-5 space-y-3">
              <div className="relative">
                <Phone
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"
                />
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  className="w-full p-4 pl-11 rounded-xl font-bold text-lg text-gray-700 outline-none bg-white border-2 border-gray-200 hover:border-gray-300 focus:border-gray-900"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="090-1234-5678"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={busy || String(phone).replace(/\D/g, "").length !== 11}
                className="w-full bg-gray-900 text-white font-black py-4 rounded-3xl disabled:opacity-50"
              >
                {busy ? <Loader /> : "認証コードを送る"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleConfirmCode} className="mt-5 space-y-3">
              <p className="text-xs font-bold text-gray-500">
                {phone} に届いた6桁のコードを入力してください
              </p>
              <input
                type="text"
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                className="w-full p-4 rounded-3xl font-bold text-2xl text-gray-700 outline-none bg-gray-50 border-2 border-gray-200 text-center tracking-widest"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                autoFocus
              />
              <button
                type="submit"
                disabled={busy || otp.length < 6}
                className="w-full bg-gray-900 text-white font-black py-4 rounded-3xl disabled:opacity-50"
              >
                {busy ? <Loader /> : "認証して利用開始"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setRegStep("PHONE");
                  setOtp("");
                  setRegError("");
                }}
                className="w-full text-sm font-bold text-gray-500 py-2"
              >
                電話番号を変更する
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  if (linked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5">
        <div className="w-full max-w-md bg-white rounded-3xl border border-gray-100 p-6 text-center">
          <div className="w-14 h-14 rounded-3xl bg-gray-50 flex items-center justify-center mx-auto mb-5">
            <Check className="text-gray-900" size={28} />
          </div>

          <h1 className="text-xl font-black text-gray-900">LINEと連携しました</h1>

          <p className="mt-3 text-sm leading-7 text-gray-600 font-medium">
            次回からはコードの入力なしでログインできます。
          </p>

          <button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            className="mt-6 w-full rounded-3xl bg-gray-900 text-white font-black py-4"
          >
            アプリを開く
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5">
        <div className="w-full max-w-md bg-white rounded-3xl border border-gray-100 p-6 text-center">
          <div className="w-14 h-14 rounded-3xl bg-amber-50 flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="text-amber-500" size={28} />
          </div>

          <h1 className="text-xl font-black text-gray-900">
            {mode === "link" ? "LINE連携ができませんでした" : "LINEログインができませんでした"}
          </h1>

          <p className="mt-3 text-sm leading-7 text-gray-600 font-medium">{error}</p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 w-full rounded-3xl bg-gray-900 text-white font-black py-4 inline-flex items-center justify-center gap-2"
          >
            <RefreshCw size={16} />
            もう一度試す
          </button>

          <button
            type="button"
            onClick={() => navigate("/auth/start", { replace: true })}
            className="mt-3 w-full rounded-3xl bg-white border-2 border-gray-200 text-gray-600 font-black py-4"
          >
            電話番号でログインする
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-3">
      <Loader />
      <p className="text-sm font-bold text-gray-500">
        {mode === "link" ? "LINEと連携しています" : "LINEでログインしています"}
      </p>
      <p className="text-[11px] font-bold text-gray-300">{stage}</p>
    </div>
  );
}
